import type { MovementId } from '../config/movements';
import { getMovement } from '../config/movements';
import type { KitGroup, WizardLine } from '../types/wizard';
import type { RfidDocumentRow } from '../services/rfid.service';
import { getDocumentItems } from './document';

/** Parse SAP Components block (Component / Item / single object). */
export function getComponentList(item: Record<string, unknown>): Record<string, unknown>[] {
  const comps = item.Components;
  if (comps == null || comps === '') return [];
  if (typeof comps !== 'object') return [];
  const container = comps as Record<string, unknown>;
  const raw = container.Component ?? container.Item ?? container.item;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  return [raw as Record<string, unknown>];
}

export function itemIsKitMaterial(item: Record<string, unknown>): boolean {
  return String(item.Material ?? item.MATERIAL ?? '')
    .trim()
    .toUpperCase()
    .startsWith('KIT');
}

export function documentHasKitStructure(doc: RfidDocumentRow): boolean {
  const items = getDocumentItems(doc);
  if (items.some((item) => getComponentList(item).length > 0)) return true;
  return items.some(itemIsKitMaterial);
}

function lineFromDocumentItem(
  item: Record<string, unknown>,
  movementId: MovementId,
  idx: number
): WizardLine {
  const m = getMovement(movementId);
  const qty = parseInt(String(item.Quantity ?? '0'), 10) || 0;
  const scannable = String(item.Scannable ?? '') === 'X';
  return {
    id: `${item.ItemNo ?? idx}-${item.Material ?? idx}`,
    itemNo: String(item.ItemNo ?? ''),
    materialCode: String(item.Material ?? ''),
    materialDesc: String(item.MaterialDesc ?? ''),
    quantity: qty,
    scannable,
    scanQty: 0,
    nonScanQty: null,
    shortQty: 0,
    fromBay: String(item.FromBay ?? ''),
    fromBin: m.fromBin ?? String(item.FromBin ?? ''),
    toBay: m.toBay ?? String(item.ToBay ?? ''),
    toBin: m.toBin ?? String(item.ToBin ?? ''),
    sender: String(item.Sender ?? ''),
    senderName: String(item.Sender_Name ?? item.SenderName ?? ''),
    materialGroupCode: String(item.Material_GC ?? item.MaterialGC ?? ''),
    materialGroupName: String(item.Material_GCN ?? item.MaterialGCN ?? ''),
    rfidCodes: [],
  };
}

function kitGroupFromParentItem(
  kitItem: Record<string, unknown>,
  componentItems: Record<string, unknown>[],
  movementId: MovementId,
  groupIndex: number
): KitGroup {
  const kitQty = parseInt(String(kitItem.Quantity ?? '1'), 10) || 1;
  const headerLine: WizardLine = {
    ...lineFromDocumentItem(kitItem, movementId, groupIndex),
    id: `kit-${groupIndex}`,
    scannable: false,
    scanQty: 0,
    nonScanQty: null,
    isKitHeader: true,
    kitQty,
  };
  const compLines = componentItems.map((c, ci) => lineFromDocumentItem(c, movementId, ci));
  return {
    kitMaterial: String(kitItem.Material ?? ''),
    kitDesc: String(kitItem.MaterialDesc ?? ''),
    kitQty,
    sender: String(kitItem.Sender ?? ''),
    senderName: String(kitItem.Sender_Name ?? kitItem.SenderName ?? ''),
    lines: [headerLine, ...compLines],
  };
}

/** Build kit groups from allot-style Items (nested Components or KIT + sibling lines). */
export function buildKitGroupsFromDocument(
  doc: RfidDocumentRow,
  movementId: MovementId
): KitGroup[] {
  const items = getDocumentItems(doc);
  const nestedParents = items.filter((item) => getComponentList(item).length > 0);

  if (nestedParents.length > 0) {
    return nestedParents.map((item, ki) =>
      kitGroupFromParentItem(item, getComponentList(item), movementId, ki)
    );
  }

  const groups: KitGroup[] = [];
  let pendingComponents: Record<string, unknown>[] = [];
  let kitItem: Record<string, unknown> | null = null;
  let groupIndex = 0;

  const flush = () => {
    if (kitItem) {
      groups.push(kitGroupFromParentItem(kitItem, pendingComponents, movementId, groupIndex));
      groupIndex += 1;
    }
    kitItem = null;
    pendingComponents = [];
  };

  for (const item of items) {
    if (itemIsKitMaterial(item)) {
      flush();
      kitItem = item;
    } else if (kitItem) {
      pendingComponents.push(item);
    }
  }
  flush();

  return groups;
}

/** Rebuild kit groups from wizard lines when headers exist but groups were lost. */
export function kitGroupsFromWizardLines(lines: WizardLine[]): KitGroup[] {
  const groups: KitGroup[] = [];
  let current: KitGroup | null = null;

  for (const line of lines) {
    if (line.isKitHeader) {
      if (current) groups.push(current);
      current = {
        kitMaterial: line.materialCode,
        kitDesc: line.materialDesc,
        kitQty: line.kitQty ?? line.quantity,
        sender: line.sender,
        senderName: line.senderName,
        lines: [line],
      };
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) groups.push(current);
  return groups;
}

export function buildLinesFromDocumentItems(
  doc: RfidDocumentRow,
  movementId: MovementId,
  variant: 'kit' | 'flat'
): { lines: WizardLine[]; kitGroups: KitGroup[] } {
  if (variant === 'kit') {
    const kitGroups = buildKitGroupsFromDocument(doc, movementId);
    if (kitGroups.length > 0) {
      return { lines: kitGroups.flatMap((g) => g.lines), kitGroups };
    }
  }
  const items = getDocumentItems(doc);
  return {
    lines: items.map((item, i) => lineFromDocumentItem(item, movementId, i)),
    kitGroups: [],
  };
}
